import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Livecoding recap #41: Towards declarative 3D scenes with React and Three.js",
  "description": "",
  "published": "2017-06-20T00:00:00.000Z",
  "redirect_from": ["/blog/livecoding-recap-41-towards-declarative-3d-scenes-react-threejs/swizec/7673"],
  "categories": "Livecoding, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding. Usually shorter than 500 words. Often with pictures. Livecoding happens almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{` on multiple channels. You should follow `}<a parentName="em" {...{
          "href": "https://www.youtube.com/channel/UCoyHgaeLLI7Knp7LDHOwZMw"
        }}>{`My Youtube`}</a>{` channel to catch me live.`}</em></p>
    <p>{`17 years ago, in 6th grade, I set out to build a 3D spinning cube and failed miserably. You can't do 3D without sine and cosine.`}</p>
    <p>{`Today, I made it happen! `}<em parentName="p">{`Two`}</em>{` 3D spinning cubes. 🤘🏼`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/06/cubes-1.gif",
        "alt": "Two spinning cubes"
      }}></img></p>
    <p>{`As promised `}<a parentName="p" {...{
        "href": "https://twitter.com/Swizec/status/876537115760222208"
      }}>{`poolside on Sunday`}</a>{`, this week's livecoding session happened on Monday. Together, we admired the speed of `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/first-day-new-2017-macbook-pro/swizec/7643"
      }}>{`my new laptop`}</a>{`, wondered why YouTube still garbles my voice, and built two green spinning 3D cubes. Declaratively 😏`}</p>
    <lite-youtube {...{
      "videoid": "eRMhOpdaKE8",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`The main render code looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`                <div class="App-intro">
                    <threescene width={800} height={600} style="{{margin:" '0="" auto'="" }}="">
                        <perspectivecamera fov={75} aspect={800/600} near={0.1} far={1000} position="{{x:" 0,="" y:="" z:="" 30}}="">

                            <cube rotation={rotation1} position="{{x:" 2,="" y:="" 0,="" z:="" 25}}="">
                            <cube 10="" rotation={rotation2} position="{{x:" -10,="" y:="" 5,="" z:="" }}="">

                        </cube></cube></perspectivecamera>
                    </threescene>
                </div>
`}</code></pre>
    <p>{`A `}<inlineCode parentName="p">{`ThreeScene`}</inlineCode>{`, which is a scene done with `}<a parentName="p" {...{
        "href": "https://threejs.org/"
      }}>{`Three.js`}</a>{`, contains a `}<inlineCode parentName="p">{`PerspectiveCamera`}</inlineCode>{`, which contains 2 `}<inlineCode parentName="p">{`Cube`}</inlineCode>{` elements. Cube positions come from state, get changed on `}<inlineCode parentName="p">{`requestAnimationFrame`}</inlineCode>{` in an infinite loop, and voilà: Two Spinning Cubes.`}</p>
    <p>{`The goal was to come up with a declarative approach to creating 3D scenes. Write React components, use all the normal React idioms, and have stuff come out in 3D.`}</p>
    <p>{`I'm sure someone's already built a React-to-Three library, but I wanted to give it a shot myself. You can think of this as a proof of concept.`}</p>
    <p>{`This is also the first time I've played with Three.js, and I have to say, it's easier than I thought it would be. At least to render two cubes. Other stuff I'm sure is super hard.`}</p>
    <p>{`Here's how it works. 👇`}</p>
    <p>{`PS: Full `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/something-3d"
      }}>{`code is on GitHub`}</a></p>
    <h2 {...{
      "id": "step-1-a-threescene"
    }}>{`Step 1: A `}<inlineCode parentName="h2">{`<ThreeScene>`}</inlineCode></h2>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class ThreeScene extends Component {
    scene = new THREE.Scene();
    renderer = new THREE.WebGLRenderer();

    componentDidMount() {
        this.updateThree(this.props);

        this.refs.anchor.appendChild(this.renderer.domElement);
    }
    componentDidUpdate() {
        this.updateThree(this.props);
    }

    updateThree(props) {
        const { width, height } = props;

        this.renderer.setSize(width, height);
    }

    getChildContext() {
        return {
            scene: this.scene,
            renderer: this.renderer
        }
    }

    render() {
        const { width, height, style } = this.props;

        return (
            <div ref="anchor" style="{[{width," height},="" style]}="">
                 {this.props.children}
            </div>
        )
    }
}

ThreeScene.childContextTypes = {
    scene: PropTypes.object,
    renderer: PropTypes.object
}
`}</code></pre>
    <p>{`A `}<inlineCode parentName="p">{`ThreeScene`}</inlineCode>{` component defines a new Three.js `}<inlineCode parentName="p">{`Scene`}</inlineCode>{` and a `}<inlineCode parentName="p">{`WebGLRenderer`}</inlineCode>{`. Both are added into React Context via `}<inlineCode parentName="p">{`getChildContext`}</inlineCode>{`.`}</p>
    <p>{`Context is messy but it gives every child access to the scene and renderer. They'll need the scene for rendering themselves, and the camera component will need access to the renderer.`}</p>
    <p>{`In `}<inlineCode parentName="p">{`componentDidMount`}</inlineCode>{`, we manually update the DOM and `}<inlineCode parentName="p">{`appendChild`}</inlineCode>{` to the rendered anchor element. This mounts our Three.js canvas to the DOM.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/06/dom-screenshot.jpg",
        "alt": null
      }}></img></p>
    <h2 {...{
      "id": "step-2-a-perspectivecamera"
    }}>{`Step 2: A `}<inlineCode parentName="h2">{`<PerspectiveCamera>`}</inlineCode></h2>
    <p>{`Three.js comes with a bunch of different camera configurations, and I don't know what they all mean. I think one of them, the StereoCamera, is meant for VR.`}</p>
    <p>{`The base example uses `}<inlineCode parentName="p">{`PerspectiveCamera`}</inlineCode>{`, so that's what I used, too. This component sets up our camera and renders our scene.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class PerspectiveCamera extends Component {
  constructor(props) {
    super(props);

    this.updateThree(props);
  }
  componentDidUpdate() {
    this.updateThree(this.props);
    this._render();
  }

  updateThree(props) {
    const { fov, aspect, near, far, position } = this.props;

    this.camera = new THREE.PerspectiveCamera(fov, aspect, near, far);
    this.camera.position.x = position.x;
    this.camera.position.y = position.y;
    this.camera.position.z = position.z;
  }

  componentDidMount() {
    this._render();
  }

  _render() {
    this.context.renderer.render(this.context.scene, this.camera);
  }

  render() {
    return <div>{this.props.children}</div>;
  }
}

PerspectiveCamera.contextTypes = {
  scene: PropTypes.object,
  renderer: PropTypes.object,
};
`}</code></pre>
    <p>{`We call `}<inlineCode parentName="p">{`updateThree`}</inlineCode>{` when `}<inlineCode parentName="p">{`<PerspectiveCamera>`}</inlineCode>{` is first initialized and whenever props or children update. This creates a new camera instance with updated properties.`}</p>
    <p>{`I'm sure Three.js supports changing field of view, aspect ratio, and other properties of an already instantiated camera, but there was no clear way of doing so. It looks like creating a new camera on every `}<inlineCode parentName="p">{`requestAnimationFrame`}</inlineCode>{` doesn't cause issues.`}</p>
    <p>{`Every time React updates our component, we call `}<inlineCode parentName="p">{`_render`}</inlineCode>{`. It performs the actual Three.js rendering of our scene.`}</p>
    <h2 {...{
      "id": "step-3-two-cubes"
    }}>{`Step 3: Two `}<inlineCode parentName="h2">{`<Cube>`}</inlineCode>{`s`}</h2>
    <p>{`Finally, our `}<inlineCode parentName="p">{`<Cube>`}</inlineCode>{` components know how to render a 3D cube from props, and add themselves to the scene when mounted. They should remove themselves when unmounted, but I didn't implement that part due to `}<em parentName="p">{`"Eh, it's just an example.”`}</em></p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class Cube extends Component {
  componentWillMount() {
    this.geometry = new THREE.BoxGeometry(1, 1, 1);
    this.material = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
    this.cube = new THREE.Mesh(this.geometry, this.material);

    this.context.scene.add(this.cube);
  }

  componentDidUpdate() {
    const { rotation, position } = this.props;

    this.cube.rotation.x = rotation.x;
    this.cube.rotation.y = rotation.y;

    this.cube.position.x = position.x;
    this.cube.position.y = position.y;
    this.cube.position.z = position.z;
  }

  render() {
    return null;
  }
}

Cube.contextTypes = {
  scene: PropTypes.object,
};
`}</code></pre>
    <p>{`Officially, a `}<inlineCode parentName="p">{`<Cube>`}</inlineCode>{` component renders `}<inlineCode parentName="p">{`null`}</inlineCode>{`. Unofficially, it adds itself to the scene with `}<inlineCode parentName="p">{`scene.add`}</inlineCode>{` in `}<inlineCode parentName="p">{`componentWillMount`}</inlineCode>{`.`}</p>
    <p>{`When it's being mounted, it also sets up the object itself with `}<inlineCode parentName="p">{`Geometry`}</inlineCode>{`, adds a green `}<inlineCode parentName="p">{`Mesh`}</inlineCode>{`, and updates its own `}<inlineCode parentName="p">{`rotation`}</inlineCode>{` and `}<inlineCode parentName="p">{`position`}</inlineCode>{` on every `}<inlineCode parentName="p">{`componentDidUpdate`}</inlineCode>{`. This creates smooth animation if we change props often enough.`}</p>
    <p>{`Every `}<inlineCode parentName="p">{`requestAnimationFrame`}</inlineCode>{` is best.`}</p>
    <h2 {...{
      "id": "fin"
    }}>{`Fin`}</h2>
    <p>{`And that's how we can render declarative 3D scenes in React. This has been a 2-hour experiment. With some more work, it could become a proper way to use Three.js with a React approach.`}</p>
    <p>{`Surely someone's already built that…`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      